/**
 * Imaging Master API
 * Handles all imaging test master data operations
 */

import http from './http';

function handleError(error) {
  const resData = error?.response?.data;
  if (resData) {
    const msg = resData.message || error?.message || 'Request failed';
    const details = Array.isArray(resData.errors)
      ? resData.errors.map(e => e.field ? `${e.field}: ${e.message}` : e.message).join('; ')
      : (resData.error || '');
    const full = details ? `${msg} — ${details}` : msg;
    throw new Error(full);
  }
  throw new Error(error?.message || 'Request failed');
}

/**
 * Get all imaging tests with filters
 * @param {Object} params - Query parameters (page, limit, modality, category, bodyPart, status, search)
 * @returns {Promise<Object>} - Paginated imaging tests
 */
export async function getImagingTests(params = {}) {
  try {
    const res = await http.get('/api/v1/imaging-master', { params });
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Create a new imaging test
 * @param {Object} testData - Imaging test data
 * @returns {Promise<Object>} - Created imaging test
 */
export async function createImagingTest(testData) {
  try {
    const res = await http.post('/api/v1/imaging-master', testData);
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get imaging test by ID
 * @param {string} id - Imaging test ID
 * @returns {Promise<Object>} - Imaging test details
 */
export async function getImagingTestById(id) {
  try {
    const res = await http.get(`/api/v1/imaging-master/${id}`);
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Update imaging test
 * @param {string} id - Imaging test ID
 * @param {Object} testData - Updated imaging test data
 * @returns {Promise<Object>} - Updated imaging test
 */
export async function updateImagingTest(id, testData) {
  try {
    const res = await http.put(`/api/v1/imaging-master/${id}`, testData);
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Delete imaging test
 * @param {string} id - Imaging test ID
 * @returns {Promise<Object>} - Deletion confirmation
 */
export async function deleteImagingTest(id) {
  try {
    const res = await http.delete(`/api/v1/imaging-master/${id}`);
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get imaging test statistics
 * @returns {Promise<Object>} - Statistics data
 */
export async function getImagingStatistics() {
  try {
    const res = await http.get('/api/v1/imaging-master/statistics');
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get available imaging modalities
 * @returns {Promise<Array>} - List of modalities
 */
export async function getImagingModalities() {
  try {
    const res = await http.get('/api/v1/imaging-master/modalities');
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get available imaging categories
 * @returns {Promise<Array>} - List of categories
 */
export async function getImagingCategories() {
  try {
    const res = await http.get('/api/v1/imaging-master/categories');
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get available body parts
 * @returns {Promise<Array>} - List of body parts
 */
export async function getBodyParts() {
  try {
    const res = await http.get('/api/v1/imaging-master/body-parts');
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Search imaging tests
 * @param {Object} params - Search parameters (modality, bodyPart, search)
 * @returns {Promise<Array>} - Search results
 */
export async function searchImagingTests(params = {}) {
  try {
    const res = await http.get('/api/v1/imaging-master/search', { params });
    return { data: res.data };
  } catch (e) {
    handleError(e);
  }
}
